import { useEffect, useState } from "react";
import "../global.css";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import { NewsItem } from "../common/news/types";
import { Helmet } from "react-helmet-async";
import { filterPublic } from "../common/news/comingSoonhelper";

/* ────────────────────────────────────────────────────────────────
   CONFIGURAZIONE TESTATA
   "recent"   → le 4 news più recenti (comportamento attuale)
   "featured" → le news marcate nel CSV con la colonna `featured`
                (numero 1–4: 1 = card grande, 2–4 = lista laterale)
   ──────────────────────────────────────────────────────────────── */
type HeaderMode = "recent" | "featured";
const HEADER_MODE = "featured" as HeaderMode;
const FEATURED_SLOTS = 4;

/**
 * Restituisce le 4 news della testata in base alla modalità.
 * `items` deve essere già ordinato per data decrescente.
 * In modalità "featured":
 *  - vengono prese le news con featured = 1..4, ordinate per quel numero;
 *  - a parità di numero (duplicato) vince la prima nel CSV;
 *  - se le featured sono meno di 4, gli slot vuoti vengono riempiti
 *    con le più recenti non già incluse (fallback che evita buchi in UI).
 */
const getHeaderNews = (items: NewsItem[]): NewsItem[] => {
  if (HEADER_MODE === "featured") {
    const seen = new Set<number>();
    const featured = items
        .filter((item) => {
          const n = Number(item.featured);
          return Number.isInteger(n) && n >= 1 && n <= FEATURED_SLOTS;
        })
        .sort((a, b) => Number(a.featured) - Number(b.featured))
        .filter((item) => {
          const n = Number(item.featured);
          if (seen.has(n)) return false;
          seen.add(n);
          return true;
        });

    if (featured.length > 0) {
      const fillers = items.filter((item) => !featured.includes(item));
      return [...featured, ...fillers].slice(0, FEATURED_SLOTS);
    }
  }
  return items.slice(0, FEATURED_SLOTS);
};

export default function NewsPage() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [headerNews, setHeaderNews] = useState<NewsItem[]>([]);
  const [newsFilteredByCategory, setNewsFilteredByCategory] = useState<NewsItem[]>([]);
  const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [seeMore, setSeeMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);


  const navigate = useNavigate();

  const handleClick = (newsItem: NewsItem) => {
    if (newsItem.link) {
      window.open(newsItem.link, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/news/${newsItem.category}/${newsItem.news_id}`, {
        state: {
          NewItem: newsItem,
          filteredNews: news.filter((item) => item.category === newsItem.category),
        },
      });
    }
  };

  useEffect(() => {
    const fetchAndSortNews = async () => {
      const response = await fetch("/csv/news_content.csv");
      const csvText = await response.text();
      Papa.parse(csvText, {
        header: true,
        complete: (result) => {
          const visibleNews = filterPublic(result.data as NewsItem[]);
          const sortedNews = visibleNews.sort(
              (a, b) => new Date(b.publication_date).getTime() - new Date(a.publication_date).getTime()
          );
          setNews(sortedNews);
          setHeaderNews(getHeaderNews(sortedNews));
          setUniqueCategories(Array.from(new Set(sortedNews.map((item) => item.category))));
          setIsLoading(false);
        },
      });
    };
    fetchAndSortNews();
  }, []);

  if (isLoading) {
    return (
        <div className="flex items-center justify-center h-screen text-white uppercase tracking-widest text-sm">
          Loading...
        </div>
    );
  }

  const displayedNews = selectedCategory ? newsFilteredByCategory : news;

  return (
      <div className="overflow-x-hidden">
        <Helmet>
          <title>News | Revolv Space</title>
          <meta name="description" content="Stay updated with the latest news and insights from Revolv Space." />
        </Helmet>

        {/* ── HEADER: MOST RECENT / FEATURED (vedi HEADER_MODE) ── */}
        {headerNews.length > 0 && (
            <div className="pt-32 pb-20 px-6 md:px-16">
              <h1
                  className="text-4xl md:text-6xl font-bold text-white text-center uppercase mb-16"
                  data-aos="fade-up"
                  data-aos-duration="600"
                  data-aos-once="true"
              >
                {HEADER_MODE === "featured" ? "Featured" : "Most Recent"}
              </h1>

              <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">

                {/* Featured image */}
                <div
                    className="cursor-pointer overflow-hidden"
                    onClick={() => handleClick(headerNews[0])}
                    data-aos="fade-up"
                    data-aos-duration="600"
                    data-aos-once="true"
                >
                  <img
                      className="w-full object-cover aspect-video hover:scale-105 transition-transform duration-500"
                      src={"/IMAGES/" + headerNews[0].news_id + "/small.png"}
                      alt={headerNews[0].titleNews}
                  />
                  <h2 className="text-white font-bold uppercase text-lg md:text-xl leading-snug mt-5 mb-2">
                    {headerNews[0].titleNews?.replace(/\\n/g, " ")}
                  </h2>
                  <p className="text-white text-opacity-50 text-sm uppercase leading-relaxed line-clamp-3">
                    {headerNews[0].subtitle?.replace(/\\n/g, " ")}
                  </p>
                  <span
                      className="text-xs uppercase tracking-widest font-bold mt-3 opacity-80"
                      style={{
                        background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                  >
              {headerNews[0].category} · {headerNews[0].publication_date}
            </span>
                </div>

                {/* Side list — slot 2, 3, 4 */}
                <div className="flex flex-col divide-y divide-white divide-opacity-10">
                  {headerNews.slice(1, 4).map((newsItem, index) => (
                      <div
                          key={index}
                          className="group cursor-pointer py-6 flex gap-5 items-center hover:bg-white hover:bg-opacity-5 transition-all duration-200 px-2"
                          onClick={() => handleClick(newsItem)}
                      >
                        <img
                            src={"/IMAGES/" + newsItem.news_id + "/small.png"}
                            alt={newsItem.titleNews}
                            className="w-40 h-24 object-cover rounded-full flex-shrink-0"
                        />
                        <div className="flex flex-col gap-1">
                      <span
                          className="text-xs uppercase tracking-widest font-bold"
                          style={{
                            background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                      >
  {newsItem.category} · {newsItem.publication_date}
</span>
                          <h3 className="text-white text-sm md:text-base font-bold uppercase leading-snug line-clamp-2 group-hover:opacity-70 transition-opacity">
                            {newsItem.titleNews?.replace(/\\n/g, " ")}
                          </h3>
                          <p className="text-white text-opacity-40 text-xs uppercase line-clamp-2 leading-relaxed">
                            {newsItem.subtitle?.replace(/\\n/g, " ")}
                          </p>
                        </div>
                      </div>
                  ))}
                </div>

              </div>
            </div>
        )}

        {/* ── DIVIDER ── */}
        <div className="bg-gradient-to-r from-gradient-start to-gradient-end h-px mx-6 md:mx-16" />

        {/* ── ALL NEWS ── */}
        <div className="pt-20 pb-10 px-6 md:px-16">
          <h2
              className="text-4xl md:text-5xl font-bold text-white uppercase mb-12 text-center"
              data-aos="fade-up"
              data-aos-duration="600"
              data-aos-once="true"
          >
            All News
          </h2>

          {/* Filtri categoria */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {[{ label: "All", value: null }, ...uniqueCategories.map((c) => ({ label: c, value: c }))].map(
                (cat, i) => {
                  const isActive = selectedCategory === cat.value;
                  return (
                      <button
                          key={i}
                          onClick={() => {
                            setSelectedCategory(cat.value);
                            if (cat.value) {
                              setNewsFilteredByCategory(news.filter((item) => item.category === cat.value));
                            }
                          }}
                          className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest font-bold transition-all duration-200 border ${
                              isActive
                                  ? "text-white border-transparent"
                                  : "text-white border-white border-opacity-20 bg-transparent hover:border-opacity-50"
                          }`}
                          style={isActive ? { background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)" } : {}}
                      >
                        {cat.label}
                      </button>
                  );
                }
            )}
          </div>

          {/* Grid news */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedNews.slice(0, seeMore ? undefined : 12).map((newsItem, index) => (
                <div
                    key={index}
                    className="cursor-pointer group"
                    data-aos="fade-up"
                    data-aos-offset="80"
                    data-aos-delay={String((index % 3) * 80)}
                    data-aos-duration="500"
                    data-aos-once="true"
                    onClick={() => handleClick(newsItem)}
                >
                  <div className="overflow-hidden">
                    <img
                        src={"/IMAGES/" + newsItem.news_id + "/small.png"}
                        alt={newsItem.titleNews}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="pt-4">
                   <span
                       className="text-xs uppercase tracking-widest font-bold opacity-80"
                       style={{
                         background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)",
                         WebkitBackgroundClip: "text",
                         WebkitTextFillColor: "transparent",
                         backgroundClip: "text",
                       }}
                   >
  {newsItem.category} · {newsItem.publication_date}
</span>
                    <h3 className="text-white font-bold uppercase text-sm md:text-base leading-snug line-clamp-2 mb-2 group-hover:opacity-70 transition-opacity">
                      {newsItem.titleNews?.replace(/\\n/g, " ")}
                    </h3>
                    <p className="text-white text-opacity-40 text-xs uppercase leading-relaxed line-clamp-2">
                      {newsItem.subtitle?.replace(/\\n/g, " ")}
                    </p>
                  </div>
                </div>
            ))}
          </div>

          {/* See more */}
          {displayedNews.length > 12 && !seeMore && (
              <div className="flex justify-center mt-14">
                <button
                    onClick={() => setSeeMore(true)}
                    className="rounded-full text-white font-bold py-3 px-10 text-sm uppercase tracking-widest transform transition-transform duration-200 hover:scale-110"
                    style={{ background: "linear-gradient(90deg, #00858F 0%, #E6007E 100%)" }}
                >
                  See More
                </button>
              </div>
          )}
        </div>
      </div>
  );
}
